import axios from 'axios';
import Cookies from 'js-cookie';
import i18n from '@/i18n/i18n';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';
import { triggerSessionExpired } from '@shared/services/authSession';
import {
    extractErrorMessage,
    shouldBlockReadAccessError,
    showBlockingError,
} from '@shared/services/blockingError';

const AUTH_SKIP_PATHS = ['/authenticate', '/refresh-token', '/signout'];

const api = axios.create({
    baseURL: import.meta.env.VITE_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        'Accept-Language': 'bg-BG',
    },
});

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedQueue = [];
}

function shouldSkipAuthHandling(config) {
    if (!config || config._skipAuthRetry || config.meta?.skipAuthRedirect) {
        return true;
    }
    const url = config.url || '';
    return AUTH_SKIP_PATHS.some((path) => url.includes(path));
}

function isNetworkError(error) {
    return (
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.message === 'Network Error'
    );
}

function notifyConnectionLost() {
    try {
        notifyError(i18n.t('common.connectionLost') || 'Connection lost. Please try again.');
    } catch (notifyErr) {
        console.error('Error sending notification:', notifyErr);
    }
}

function handleSessionExpired() {
    try {
        notifyError(i18n.t('common.sessionExpired') || 'Session expired. Please log in again.');
    } catch (notifyErr) {
        console.error('Error sending notification:', notifyErr);
    }

    if (!triggerSessionExpired()) {
        Cookies.remove('accessToken', { path: '/' });
        Cookies.remove('refreshToken', { path: '/' });
        Cookies.remove('specialRights', { path: '/' });
        setTimeout(() => {
            window.location.href = '/login';
        }, 1000);
    }
}

function setAccessTokenCookie(accessToken) {
    const isProduction = import.meta.env.MODE === 'production';
    Cookies.set('accessToken', accessToken, {
        secure: isProduction,
        sameSite: 'Strict',
        path: '/',
        expires: 1 / 48,
    });
}

async function refreshAccessToken() {
    const refreshToken = Cookies.get('refreshToken');
    if (!refreshToken) {
        throw new Error('No refresh token');
    }

    const response = await api.post('/refresh-token', refreshToken, {
        headers: { 'Content-Type': 'text/plain' },
        _skipAuthRetry: true,
    });

    const newAccessToken = response.data;
    if (!newAccessToken) {
        throw new Error('Invalid refresh response');
    }

    setAccessTokenCookie(newAccessToken);
    return newAccessToken;
}

api.interceptors.request.use(
    async (config) => {
        const token = Cookies.get('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['Accept-Language'] = i18n.language || 'bg-BG';

        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const wasLoggedIn = Boolean(Cookies.get('accessToken') || Cookies.get('refreshToken'));

        if (isNetworkError(error)) {
            if (wasLoggedIn) {
                notifyConnectionLost();
            }
            return Promise.reject(error);
        }

        if (shouldBlockReadAccessError(error, originalRequest)) {
            showBlockingError(extractErrorMessage(error));
            return Promise.reject(error);
        }

        if (error.response?.status !== 401 || shouldSkipAuthHandling(originalRequest)) {
            return Promise.reject(error);
        }

        if (originalRequest._retry) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const newAccessToken = await refreshAccessToken();
            processQueue(null, newAccessToken);
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return api(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError, null);
            if (wasLoggedIn) {
                handleSessionExpired();
            }
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;
